import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { CUSTOMAPIService } from 'app/custom-api.service';

export interface EntityIntegrationRecord {
  id: string;
  rootEntityId: string;
  subEntityId: string;
  integrationType: string;
  filePath?: string;
  status?: string;
  isEnable?: boolean;
  isAuth?: boolean;
  appliedToAll?: boolean;
  currView?: string;
  webhookUpdatedAt?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ResolvedIntegration {
  record: EntityIntegrationRecord;
  source: 'specific' | 'appliedToAll';
}

@Injectable({ providedIn: 'root' })
export class IntegrationMappingService {
  private _cache: { [rootEntityId: string]: EntityIntegrationRecord[] } = {};
  private _subEntityCache: { [subEntityId: string]: EntityIntegrationRecord[] } = {};
  private _inFlight: { [key: string]: Promise<EntityIntegrationRecord[]> } = {};

  /** Records grouped by subEntityId — built from the root fetch */
  private _entityMap: { [rootEntityId: string]: { [subEntityId: string]: EntityIntegrationRecord[] } } = {};

  /** Emits after any mutation so consumers can refresh UI */
  readonly onMappingsChanged = new Subject<{ rootEntityId: string }>();

  constructor(private customApi: CUSTOMAPIService) {}

  /**
   * Returns true if the record's filePath is sub-entity-scoped
   * (i.e. the path contains /INTEGRATIONS/{subEntityId}/).
   * Multi-entity records share the root path even though they have a subEntityId.
   */
  static isSubEntityScopedRecord(record: EntityIntegrationRecord): boolean {
    if (!record.subEntityId || !record.filePath) {
      return false;
    }
    return record.filePath.includes(`/INTEGRATIONS/${record.subEntityId}/`);
  }

  // ─── Public Query Methods ────────────────────────────────────────────────────

  /**
   * Get all integration mappings for a root entity.
   * Returns all records (appliedToAll + per-sub-entity) for cache/resolution.
   */
  async getMappingsForRoot(rootEntityId: string, forceRefresh = false): Promise<EntityIntegrationRecord[]> {
    if (!rootEntityId) {
      return [];
    }

    if (!forceRefresh && this._cache[rootEntityId]) {
      return this._cache[rootEntityId];
    }

    const cacheKey = `root_${rootEntityId}`;
    if (!this._inFlight[cacheKey]) {
      this._inFlight[cacheKey] = this._fetchByRootEntityId(rootEntityId);
    }

    try {
      const result = await this._inFlight[cacheKey];
      this._cache[rootEntityId] = result;
      this._buildEntityMap(rootEntityId, result);
      return result;
    } finally {
      delete this._inFlight[cacheKey];
    }
  }

  /**
   * Get integrations that are applied to all sub-entities (root-level status cards).
   * Only returns records stored at the root level in the entity map with appliedToAll = true.
   */
  async getAppliedToAllForRoot(rootEntityId: string): Promise<EntityIntegrationRecord[]> {
    await this.getMappingsForRoot(rootEntityId);
    const rootRecords = this._entityMap[rootEntityId]?.[rootEntityId] || [];
    return rootRecords.filter(m => m.appliedToAll === true && m.isEnable !== false);
  }

  /**
   * Single entry point for card status resolution.
   * - "All Frameworks" (entityId === rootEntityId or no subEntityId): returns appliedToAll from root cache.
   * - Any other card (child entity): returns that entity's specific integrations + appliedToAll from root.
   */
  async getIntegrationsForEntity(
    entityId: string,
    rootEntityId: string,
    forceRefresh = false
  ): Promise<EntityIntegrationRecord[]> {
    await this.getMappingsForRoot(rootEntityId, forceRefresh);
    let rootRecords;
    // Root / "All Frameworks" — show all integration types (appliedToAll + multi-entity)
    // Exclude sub-entity-scoped records (those with subEntityId in filePath)
    const result: EntityIntegrationRecord[] = [];
    const seenTypes = new Set<string>();

    if (!entityId || entityId === rootEntityId) {
      const allMappings = this._cache[rootEntityId] || [];

      // appliedToAll records take priority (show root-level status)
      rootRecords = this._entityMap[rootEntityId]?.[rootEntityId] || [];
      for (const record of rootRecords) {
        if (record.appliedToAll && record.isEnable !== false) {
          result.push(record);
          seenTypes.add(record.integrationType);
        }
      }

      // Also include per-sub-entity integrations not covered by appliedToAll,
      // but only if they use the shared root filepath (multi-entity selection).
      // Sub-entity-scoped records (filePath contains the subEntityId) are excluded
      // — they belong to that specific entity's tab only.
      for (const record of allMappings) {
        if (
          !seenTypes.has(record.integrationType) &&
          record.isEnable !== false &&
          !IntegrationMappingService.isSubEntityScopedRecord(record)
        ) {
          result.push(record);
          seenTypes.add(record.integrationType);
        }
      }

      return result;
    }

    // Child entity — merge entity-specific + appliedToAll from root
    const entityRecords = this._entityMap[rootEntityId]?.[entityId] || [];
    rootRecords = (this._entityMap[rootEntityId]?.[rootEntityId] || []).filter(
      m => m.appliedToAll === true && m.isEnable !== false
    );

    // Entity-specific records take priority
    for (const record of entityRecords) {
      if (record.isEnable !== false) {
        result.push(record);
        seenTypes.add(record.integrationType);
      }
    }

    // Add appliedToAll from root that aren't overridden
    for (const record of rootRecords) {
      if (!seenTypes.has(record.integrationType)) {
        result.push(record);
      }
    }

    return result;
  }

  /**
   * Get integrations for a specific sub-entity.
   * Uses the cached entity map built from the root fetch — no extra network call needed.
   * Returns: sub-entity specific records + appliedToAll records from root.
   */
  async getIntegrationsForSubEntity(
    subEntityId: string,
    rootEntityId: string,
    forceRefresh = false
  ): Promise<ResolvedIntegration[]> {
    // Ensure root data is loaded (builds the entity map)
    const rootMappings = await this.getMappingsForRoot(rootEntityId, forceRefresh);
    const appliedToAllMappings = rootMappings.filter(m => m.appliedToAll === true && m.isEnable !== false);

    // Get sub-entity specific records from the entity map (already cached from root fetch)
    const subEntityMappings = this._entityMap[rootEntityId]?.[subEntityId] || [];

    const result: ResolvedIntegration[] = [];
    const seenTypes = new Set<string>();

    // Sub-entity specific records take priority
    for (const record of subEntityMappings) {
      if (record.isEnable !== false) {
        result.push({ record, source: 'specific' });
        seenTypes.add(record.integrationType);
      }
    }

    // Add appliedToAll records that aren't overridden by sub-entity specific ones
    for (const record of appliedToAllMappings) {
      if (!seenTypes.has(record.integrationType)) {
        result.push({ record, source: 'appliedToAll' });
      }
    }

    return result;
  }

  /**
   * Get all connected (authenticated) integration types for a root entity.
   */
  async getConnectedIntegrationTypes(rootEntityId: string): Promise<string[]> {
    const allMappings = await this.getMappingsForRoot(rootEntityId);
    const enabled = allMappings.filter(m => m.isEnable !== false && m.isAuth);
    return [...new Set(enabled.map(m => m.integrationType))];
  }

  /**
   * Check if any integration is authenticated for this root (used for tab visibility).
   */
  async hasAnyAuthenticatedIntegration(rootEntityId: string): Promise<boolean> {
    const allMappings = await this.getMappingsForRoot(rootEntityId);
    return allMappings.some(m => m.isAuth && m.isEnable !== false);
  }

  /**
   * Check if any integration is authenticated for a specific sub-entity.
   */
  async hasAuthenticatedIntegrationForSubEntity(subEntityId: string, rootEntityId: string): Promise<boolean> {
    const resolved = await this.getIntegrationsForSubEntity(subEntityId, rootEntityId);
    return resolved.some(r => r.record.isAuth);
  }

  /**
   * Get the EntityIntegration record IDs for a service type (for status updates).
   */
  async getRecordIdsForService(rootEntityId: string, integrationType: string): Promise<string[]> {
    const allMappings = await this.getMappingsForRoot(rootEntityId);
    return allMappings.filter(m => m.integrationType === integrationType).map(m => m.id);
  }

  // ─── Cache Management ────────────────────────────────────────────────────────

  invalidateCache(rootEntityId: string): void {
    delete this._cache[rootEntityId];
    delete this._entityMap[rootEntityId];
    this.onMappingsChanged.next({ rootEntityId });
  }

  invalidateSubEntityCache(subEntityId: string): void {
    delete this._subEntityCache[subEntityId];
  }

  invalidateAll(): void {
    this._cache = {};
    this._subEntityCache = {};
    this._entityMap = {};
  }

  /**
   * Get the cached entity map for a root entity (already fetched).
   * Keys are subEntityIds, values are their specific records.
   */
  getEntityMap(rootEntityId: string): { [subEntityId: string]: EntityIntegrationRecord[] } | null {
    return this._entityMap[rootEntityId] || null;
  }

  // ─── Private ─────────────────────────────────────────────────────────────────

  /**
   * Builds a mapper object keyed by subEntityId from the flat records list.
   * appliedToAll records are stored under the rootEntityId key.
   */
  private _buildEntityMap(rootEntityId: string, records: EntityIntegrationRecord[]): void {
    const map: { [subEntityId: string]: EntityIntegrationRecord[] } = {};
    for (const record of records) {
      const key = record.subEntityId || rootEntityId;
      if (!map[key]) {
        map[key] = [];
      }
      map[key].push(record);
    }
    this._entityMap[rootEntityId] = map;
  }

  private async _getSubEntityMappings(subEntityId: string, forceRefresh = false): Promise<EntityIntegrationRecord[]> {
    if (!subEntityId) {
      return [];
    }

    if (!forceRefresh && this._subEntityCache[subEntityId]) {
      return this._subEntityCache[subEntityId];
    }

    const cacheKey = `sub_${subEntityId}`;
    if (!this._inFlight[cacheKey]) {
      this._inFlight[cacheKey] = this._fetchBySubEntityId(subEntityId);
    }

    try {
      const result = await this._inFlight[cacheKey];
      this._subEntityCache[subEntityId] = result;
      return result;
    } finally {
      delete this._inFlight[cacheKey];
    }
  }

  private async _fetchByRootEntityId(rootEntityId: string): Promise<EntityIntegrationRecord[]> {
    try {
      const items: EntityIntegrationRecord[] = [];
      let nextToken: string | null = null;

      do {
        const response: any = await this.customApi.EntityIntegrationsByRootEntityId(
          rootEntityId,
          null,
          null,
          500,
          nextToken
        );
        if (response?.items) {
          items.push(...response.items);
        }
        nextToken = response?.nextToken || null;
      } while (nextToken);

      return items;
    } catch (e) {
      console.error('IntegrationMappingService._fetchByRootEntityId error:', e);
      return [];
    }
  }

  private async _fetchBySubEntityId(subEntityId: string): Promise<EntityIntegrationRecord[]> {
    try {
      const items: EntityIntegrationRecord[] = [];
      let nextToken: string | null = null;

      do {
        const response: any = await this.customApi.EntityIntegrationsBySubEntityId(
          subEntityId,
          null,
          null,
          null,
          500,
          nextToken
        );
        if (response?.items) {
          items.push(...response.items);
        }
        nextToken = response?.nextToken || null;
      } while (nextToken);

      return items;
    } catch (e) {
      console.error('IntegrationMappingService._fetchBySubEntityId error:', e);
      return [];
    }
  }
}
